import { useRef } from 'react';
import {
  IconCommand, IconGear, IconImage, IconSaveImage, IconChat, IconExit, IconPrint,
  IconShare, IconPlay,
} from '../icons';

/**
 * The ⌘ cluster in the top-left. Collapsed it is a single orb; tapping it
 * unrolls the row of actions.
 *
 * On a phone the row would run off the edge of the screen, so below 640px the
 * same buttons lay themselves out as a grid panel underneath the orb instead
 * (see `.pill--command` in global.css). Same markup, same order — only the
 * shape changes.
 */
export default function CommandBar({
  open, onToggle, onSettings, onInsertImage, onExport, onChat, onLeave, onPrint, onShare,
  onReplay, chatOpen, chatEnabled, unread,
}: {
  open: boolean;
  onToggle: () => void;
  onSettings: () => void;
  onInsertImage: (file: File) => void;
  onExport: () => void;
  onChat: () => void;
  onLeave: () => void;
  onPrint: () => void;
  onShare: () => void;
  onReplay: () => void;
  chatOpen: boolean;
  chatEnabled: boolean;
  unread: number;
}) {
  const fileInput = useRef<HTMLInputElement>(null);

  return (
    <div className="pill pill--command">
      <button
        type="button"
        className="pill-btn"
        data-active={open}
        onClick={onToggle}
        aria-expanded={open}
        aria-label={open ? 'Hide menu' : 'Show menu'}
      >
        <IconCommand />
      </button>

      <div className="expando" data-open={open}>
        <button type="button" className="pill-btn" onClick={onSettings} aria-label="Settings" title="Settings">
          <IconGear />
        </button>

        <button
          type="button"
          className="pill-btn"
          onClick={() => fileInput.current?.click()}
          aria-label="Add an image"
          title="Add an image"
        >
          <IconImage />
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onInsertImage(file);
            e.target.value = '';
          }}
        />

        <button
          type="button"
          className="pill-btn"
          onClick={onExport}
          aria-label="Save the drawing as an image"
          title="Save as an image"
        >
          <IconSaveImage />
        </button>

        <button
          type="button"
          className="pill-btn"
          onClick={onChat}
          data-active={chatOpen}
          disabled={!chatEnabled}
          aria-label="Chat"
          title={chatEnabled ? 'Chat' : 'Chat is off for this sketchpad'}
          style={{ position: 'relative' }}
        >
          <IconChat />
          {unread > 0 && !chatOpen ? (
            <span className="pill-badge">{unread > 9 ? '9+' : unread}</span>
          ) : null}
        </button>

        <button type="button" className="pill-btn" onClick={onReplay} aria-label="Replay the drawing" title="Replay the drawing">
          <IconPlay />
        </button>

        <button type="button" className="pill-btn" onClick={onPrint} aria-label="Print" title="Print">
          <IconPrint />
        </button>

        <button type="button" className="pill-btn" onClick={onShare} aria-label="Share" title="Share">
          <IconShare />
        </button>

        <span className="pill-sep" />

        <button
          type="button"
          className="pill-btn"
          onClick={onLeave}
          aria-label="Leave this sketchpad"
          title="Leave — back to my sketchpads"
        >
          <IconExit />
        </button>
      </div>
    </div>
  );
}
